<template>
  <v-container class="ma-0 pa-0" fluid>
    <router-view/>
    <v-footer v-if="isMenuDrawerClosable()" fixed class="pa-0 navigation-footer">
      <v-row>
        <v-tabs
          grow
          dark
          background-color="secondary"
          slider-color="info"
          active-class="activeTab"
        >
          <v-tab to="clock">{{ $t('clock.clock') }}</v-tab>
          <v-tab to="time-pairs">{{ $t('indicators.history') }}</v-tab>
        </v-tabs>
      </v-row>
    </v-footer>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';

import cloneDeep from 'lodash/cloneDeep';

import constants from '@/constants';
import EventBus from '@/event-bus';

import canClockShiftIn from '@/mixins/clocking/individual/canClockShiftIn';
import canClockShiftOut from '@/mixins/clocking/individual/canClockShiftOut';
import canClockSegmentIn from '@/mixins/clocking/individual/canClockSegmentIn';
import canClockSegmentOut from '@/mixins/clocking/individual/canClockSegmentOut';
import displayAdaptable from '@/mixins/displayAdaptable';
import hasAppSnackbar from '@/mixins/hasAppSnackbar';
import DateTimeUtil from '@/util/date-time-util';

export default {
  name: 'Employee',
  mixins: [
    canClockShiftIn,
    canClockShiftOut,
    canClockSegmentIn,
    canClockSegmentOut,
    displayAdaptable,
    hasAppSnackbar,
  ],

  data() {
    return {
      delayTimer: null,
      timePairBackup: null,
      undoNavigationNext: null,
    };
  },

  computed: {
    ...mapState(['activeTimePair', 'activeSegmentType']),
  },

  methods: {
    ...mapActions(['updateTimer']),
    ...mapMutations([
      'resetActiveTimePair',
      'setActiveTimePair',
      'setPageTitle',
      'setActiveSegmentType',
    ]),

    addUnloadEventListener(clockOutHandler) {
      const resolver = (event) => {
        if (this.delayTimer) {
          event.preventDefault();
          event.returnValue = '';

          clearTimeout(this.delayTimer);
          this.delayTimer = null;

          clockOutHandler();
        }
      };

      window.addEventListener('beforeunload', resolver);
    },

    undoClockOut() {
      this.setActiveTimePair(this.timePairBackup);
      this.updateTimer(this.activeTimePair.clockIn);

      clearTimeout(this.delayTimer);
      this.delayTimer = null;
    },

    displayAlreadyClockedInIndicator(responseData) {
      if (responseData.isAlreadyRunningTimePair) {
        const dateClockedIn = new Date(responseData.clockIn.date);
        this.showAppErrorDialog({
          message: this.$t('dialogs.error.causes.segmentAlreadyClockedIn',
            { time: DateTimeUtil.getFormattedHoursMinutes(dateClockedIn) }),
        });
      }
    },
  },

  created() {
    this.setPageTitle(this.$t('titles.recordTime'));

    EventBus.$on('clock:create-clock-in', (payload) => {
      const successCallback = (responseData, isCloningTimePair) => {
        this.setActiveTimePair(responseData);

        const currentSegment = responseData.segmentTimePairs.reverse()
        .find((segment) => !segment.clockOut);

        this.setActiveSegmentType(currentSegment ? currentSegment.pairType : constants.TimePairType.SHIFT);
        this.updateTimer(currentSegment ? currentSegment.clockIn : responseData.clockIn);
        this.displayAlreadyClockedInIndicator(responseData);

        this.showSnackbar({
          text: isCloningTimePair ? this.$t('snackbar.clockedInSettings')
            : this.$t('snackbar.youClockInSuccess'),
          color: constants.AppColors.SUCCESS,
        });
      };

      this.createShiftClockIn(
        payload.timePairCustomFieldValues,
        payload.selectedAssignment.id,
        payload.selectedTimeType ? payload.selectedTimeType.id : null,
        successCallback,
        payload.lastly,
        false,
        true,
        payload.isCloningTimePair,
      );
    });

    EventBus.$on('clock:create-clock-out', (payload) => {
      const successCallback = (responseData) => {
        this.setActiveSegmentType(constants.TimePairType.SHIFT);
        this.setActiveTimePair(responseData);

        if (this.activeTimePair.clockOut) {
          this.resetActiveTimePair();
        }

        if (this.undoNavigationNext) {
          this.undoNavigationNext();
          this.undoNavigationNext = null;
        }
      };

      const clockOutHandler = () => this.createShiftClockOut(
        payload.timePair,
        payload.timePair.employeeAssignment.id,
        successCallback,
        payload.lastly,
        false,
        true,
      );

      this.showSnackbar({
        text: this.$t('snackbar.youClockOutSuccess'),
        color: constants.AppColors.SUCCESS,
        buttonText: constants.CLOCK_OUT_DELAY_IN_MILLISECONDS > 0 ? this.$t('misc.undo') : null,
        buttonHandler: () => {
          this.undoClockOut();
          if (payload.lastly) payload.lastly();
        },
      });

      this.timePairBackup = cloneDeep(this.activeTimePair);
      this.resetActiveTimePair();
      this.addUnloadEventListener(clockOutHandler);

      if (constants.CLOCK_OUT_DELAY_IN_MILLISECONDS > 0) {
        this.delayTimer = setTimeout(() => {
          clockOutHandler();
          this.delayTimer = null;
        }, constants.CLOCK_OUT_DELAY_IN_MILLISECONDS);
      } else {
        clockOutHandler();
      }
    });

    EventBus.$on('clock:create-segment-clock-in', (payload) => {
      this.showSnackbar({ text: this.$t('snackbar.onBreak'), color: constants.AppColors.PRIMARY });

      const successCallback = (responseData) => {
        this.activeTimePair.segmentTimePairs.push(responseData);
        this.setActiveTimePair(this.activeTimePair);
        this.setActiveSegmentType(responseData.pairType);
        this.updateTimer(responseData.clockIn);
        this.displayAlreadyClockedInIndicator(responseData);
      };

      const errorResetCallback = () => {
        this.resetActiveTimePair();
      };

      this.createSegmentClockIn(
        payload.timePair,
        payload.timePair.employeeAssignment.id,
        payload.pairType,
        successCallback,
        payload.lastly,
        false,
        true,
        errorResetCallback,
      );
    });

    EventBus.$on('clock:create-segment-clock-out', (payload) => {
      this.showSnackbar({
        text: this.$t('snackbar.notOnBreak'),
        color: constants.AppColors.PRIMARY,
      });

      const successCallback = (responseData) => {
        if (responseData.duration < 1) {
          this.showAppWarningDialog({
            message: this.$t('dialogs.warning.breakDiscarded.message'),
          });
        }

        const lastIndex = this.activeTimePair.segmentTimePairs.length - 1;
        this.activeTimePair.segmentTimePairs[lastIndex] = responseData;
        this.setActiveTimePair(this.activeTimePair);
        this.setActiveSegmentType(constants.TimePairType.SHIFT);
        this.updateTimer(this.activeTimePair.clockIn);
      };

      const errorResetCallback = (errorResponse) => {
        if (errorResponse.data.isSegmentTimePair && errorResponse.data.isShiftTimePairCompleted) {
          // todo here may be a issue with re clock in after break and shift already clocked out
          this.resetActiveTimePair();
        } else {
          this.setActiveTimePair(this.activeTimePair);
          this.setActiveSegmentType(constants.TimePairType.SHIFT);
          this.updateTimer(this.activeTimePair.clockIn);
        }
      };

      this.createSegmentClockOut(
        payload.timePair,
        payload.selectedAssignment.id,
        successCallback,
        payload.lastly,
        false,
        true,
        errorResetCallback,
      );
    });
  },

  beforeDestroy() {
    EventBus.$off('clock:create-clock-in');
    EventBus.$off('clock:create-clock-out');
    EventBus.$off('clock:create-segment-clock-in');
    EventBus.$off('clock:create-segment-clock-out');
  },

  beforeRouteLeave(to, from, next) {
    if (this.delayTimer) {
      this.undoNavigationNext = next;
      window.dispatchEvent(new Event('beforeunload'));
      return;
    }
    next();
  },
};
</script>

<style scoped>
.navigation-footer {
  z-index: 2;
  box-shadow: 0 -2px 8px 0 rgba(0, 0, 0, .12);
}
</style>
